import { reactive, ref } from 'vue';
import { skapi } from '@/main';
import { serviceList, serviceIdList, endOfList } from '@/views/project-list';

import { miscUpdater } from '@/code/misc';
import {
	beginMcpOAuthOnLogin,
	getStoredMcpToken,
	clearStoredMcpToken,
} from '@/code/mcp_oauth';

export let user: { [key: string]: any } = reactive({});
export let customer: Promise<Customer> = null;
let lastLoggedInUserId: string | null = null;

type Customer = {
	id: string;
	object: 'customer';
	address: {
		city: string;
		country: string;
		line1: string;
		line2: string;
		postal_code: string;
		state: string;
	};
	balance: number;
	created: number;
	currency: string;
	default_source: any;
	delinquent: boolean;
	description: any;
	discount: any;
	email: string;
	invoice_prefix: string;
	invoice_settings: {
		custom_fields: any;
		default_payment_method: any;
		footer: any;
		rendering_options: any;
	};
	livemode: boolean;
	metadata: {
		[key: string]: any;
	};
	name: string;
	next_invoice_sequence: number;
	phone: any;
	preferred_locales: any[];
	shipping: any;
	tax_exempt: string;
	test_clock: any;
};

export let initUser = (userIncoming: any) => {
	for (let k in user) {
		delete user[k];
	}
	if (userIncoming) {
		for (let k in userIncoming) {
			user[k] = userIncoming[k];
		}
	}
};

const getJwtSub = (jwt?: string): string | null => {
	if (!jwt || typeof jwt !== 'string') return null;
	const parts = jwt.split('.');
	if (parts.length !== 3 || !parts[1]) return null;
	try {
		const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const padding = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
		const payload = JSON.parse(atob(b64 + padding));
		return typeof payload?.sub === 'string' ? payload.sub : null;
	} catch {
		return null;
	}
};

let newsletterSubscriptionStatus: Promise<boolean> | null = null;
export function getNewsletterSubscriptionStatus() {
	if (newsletterSubscriptionStatus === null) {
		newsletterSubscriptionStatus = skapi
			.getNewsletterSubscription({
				group: 'authorized',
			})
			.then((subs) => {
				return !!subs.length;
			});
	}
	return newsletterSubscriptionStatus;
}

export function setNewsletterSubscriptionStatus(status: boolean) {
	if (status) {
		return skapi
			.subscribeNewsletter({
				group: 'authorized',
			})
			.then(() => {
				newsletterSubscriptionStatus = Promise.resolve(true);
			});
	} else {
		return skapi
			.unsubscribeNewsletter({
				group: 'authorized',
			})
			.then(() => {
				newsletterSubscriptionStatus = Promise.resolve(false);
			});
	}
}

export let userLoginCallback = async (userIncoming: any) => {
	// console.log('User login callback triggered with user:', userIncoming);
	if (userIncoming) {
		const incomingUserId = typeof userIncoming?.user_id === 'string' ? userIncoming.user_id : null;
		if (incomingUserId && lastLoggedInUserId && incomingUserId !== lastLoggedInUserId) {
			// User switched accounts within the same SPA lifecycle.
			// Clear project caches so route-resolved services cannot be reused
			// from a previous identity.
			while (serviceIdList.value.length > 0) {
				serviceIdList.value.pop();
			}
			for (let k in serviceList) {
				delete serviceList[k];
			}
			endOfList.value = false;
		}
		lastLoggedInUserId = incomingUserId;

		let userIncomingMisc = JSON.parse(userIncoming.misc || '{}');

		getStripeCustomer(userIncoming);

		// auto subscribe google users to newsletter
		if (
			userIncoming?.signup_ticket &&
			userIncoming?.signup_ticket === 'OIDPASS' &&
			!userIncomingMisc.googleUserSubscribed
		) {
			await skapi.subscribeNewsletter({
				group: 'authorized',
			});
			userIncomingMisc.googleUserSubscribed = true;
			miscUpdater({ googleUserSubscribed: true });
		}

		// Ensure the MCP server has a live session file for this user.
		// Runs for fresh logins AND autoLogin session restores. Skipped when
		// an OAuth flow is already in flight (mid-callback or inbound skapi
		// OAuth) so we don't clobber it.
		try {
			const params = new URLSearchParams(window.location.search);
			// Inbound platform OAuth: e.g. ChatGPT redirected the user to
			// /login?oauth=platform&redirect_uri=...&state=... to authenticate
			// against skapi. login.vue owns that handshake (returnOAuthToMCP)
			// and must keep the query params intact. If we let the MCP token
			// refresh kick off here, the round-trip would either consume those
			// params or rewrite redirectAfter and the user would never be
			// returned to the calling platform. Skip the refresh; it can run
			// on the user's next visit.
			const inboundPlatformFlow =
				params.get('oauth') === 'platform' &&
				!!params.get('redirect_uri') &&
				!!params.get('state');
			const inflight =
				!!sessionStorage.getItem('mcp_oauth:state') ||
				params.get('oauth') === 'skapi' ||
				inboundPlatformFlow ||
				(!!params.get('code') && !!params.get('state'));

			if (!inflight) {
				const tok = getStoredMcpToken();
				const now = Date.now();
				const tokenSub = getJwtSub(tok?.access_token);
				const currentUserSub = typeof userIncoming?.user_id === 'string'
					? userIncoming.user_id
					: null;
				const expired =
					!tok ||
					(typeof tok.expires_at === 'number' && tok.expires_at < now + 60_000);
				const mismatchedUser =
					!!tok &&
					!!currentUserSub &&
					!!tokenSub &&
					tokenSub !== currentUserSub;

				if (expired || mismatchedUser) {
					clearStoredMcpToken();
					const pathname = window.location.pathname;
					const here =
						pathname + window.location.search + window.location.hash;
					// If the user is sitting on a guest-style entry route (landing,
					// login, signup) when auto-login resolves and the MCP token is
					// stale, kicking off the OAuth round-trip would store `/` (or
					// `/login`) as `redirectAfter` and drop them right back on the
					// landing page after the callback completes — which then looks
					// like clicking "Go to my projects" silently bounces back to
					// landing. For those entry routes use `/my-projects` as the
					// post-OAuth destination so logged-in users always land in the
					// app shell. Deep links (e.g. /my-projects/<id>/agent) keep
					// their exact path so the user resumes where they were.
					const isGuestEntryRoute =
						pathname === '/' ||
						pathname === '/login' ||
						pathname === '/signup';
					const redirectAfter = isGuestEntryRoute ? '/my-projects' : here;
					await beginMcpOAuthOnLogin(redirectAfter);
					// browser is leaving the SPA
					return;
				}
			}
		} catch (err) {
			console.error('MCP OAuth bootstrap (userLoginCallback) failed', err);
			// non-fatal: continue rest of callback
		}
	} else {
		lastLoggedInUserId = null;
		while (serviceIdList.value.length > 0) {
			serviceIdList.value.pop();
		}
		for (let k in serviceList) {
			delete serviceList[k];
		}

		endOfList.value = false;
	}
};

let getStripeCustomer = async (user: any) => {
	let misc;

	if (user?.misc && typeof user.misc === 'string') {
		misc = JSON.parse(user.misc);
	} else {
		misc = {};
	}

	if (misc?.stripe_customer_id) {
		// stripe_customer_id exists

		let customer_id = misc.stripe_customer_id;

		// get customer info, and update customer.value
		customer = skapi.clientSecretRequest({
			clientSecretName: 'stripe_test',
			url: `https://api.stripe.com/v1/customers/${customer_id}`,
			method: 'GET',
			headers: {
				Authorization: 'Bearer $CLIENT_SECRET',
			},
		});
	} else {
		// stripe_customer_id does not exist

		// create customer, save customer id in user misc
		customer = skapi.clientSecretRequest({
			clientSecretName: 'stripe_test',
			url: 'https://api.stripe.com/v1/customers',
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Authorization: 'Bearer $CLIENT_SECRET',
			},
			data: {
				name: user.name || user.email,
				email: user.email,
			},
		});

		// update customer id in user misc
		customer.then(async (c) => {
			if (c) {
				misc.stripe_customer_id = c.id;
				miscUpdater({
					stripe_customer_id: misc.stripe_customer_id,
				});
			}
		});
	}
};
